
import React from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useProductAnalytics } from '@/hooks/useProductAnalytics';
import { Loader2, TrendingUp, ShoppingBag, DollarSign, AlertTriangle, Package } from 'lucide-react';
import ProductAnalyticsCard from '../products/ProductAnalyticsCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const ProductAnalyticsDashboard = () => {
  const {
    topProducts,
    lowStockProducts,
    metrics,
    isLoading
  } = useProductAnalytics();

  const { data: products, isLoading: isProductsLoading } = useQuery({
    queryKey: ['products-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories (name),
          product_analytics (
            views_count,
            sales_count,
            revenue
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const { data: categoryStats } = useQuery({
    queryKey: ['category-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          category_id,
          categories (name),
          product_analytics (revenue, sales_count)
        `);
      
      if (error) throw error;
      
      const categoryMap = new Map();
      data.forEach(product => {
        const categoryName = product.categories?.name || 'Uncategorized';
        const revenue = product.product_analytics?.reduce((sum: number, analytics: any) => sum + (analytics.revenue || 0), 0) || 0;
        const sales = product.product_analytics?.reduce((sum: number, analytics: any) => sum + (analytics.sales_count || 0), 0) || 0;
        
        if (categoryMap.has(categoryName)) {
          const existing = categoryMap.get(categoryName);
          categoryMap.set(categoryName, {
            name: categoryName,
            revenue: existing.revenue + revenue,
            sales: existing.sales + sales,
            products: existing.products + 1
          });
        } else {
          categoryMap.set(categoryName, {
            name: categoryName,
            revenue,
            sales,
            products: 1
          });
        }
      });
      
      return Array.from(categoryMap.values());
    },
  });

  if (isLoading || isProductsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const totalProducts = products?.length || 0;
  const activeProducts = products?.filter(p => p.status === 'active').length || 0;
  const lowStockCount = products?.filter(p => (p.stock_quantity || 0) <= 10).length || 0;
  const outOfStockProducts = products?.filter(p => (p.stock_quantity || 0) === 0).length || 0;

  const totalRevenue = products?.reduce((sum, product) => {
    const productRevenue = product.product_analytics?.reduce((pSum: number, analytics: any) => pSum + (analytics.revenue || 0), 0) || 0;
    return sum + productRevenue;
  }, 0) || 0;

  const totalSales = products?.reduce((sum, product) => {
    const productSales = product.product_analytics?.reduce((pSum: number, analytics: any) => pSum + (analytics.sales_count || 0), 0) || 0;
    return sum + productSales;
  }, 0) || 0;

  const topProductsData = products
    ?.map(product => ({
      ...product,
      totalRevenue: product.product_analytics?.reduce((sum: number, analytics: any) => sum + (analytics.revenue || 0), 0) || 0,
      totalSales: product.product_analytics?.reduce((sum: number, analytics: any) => sum + (analytics.sales_count || 0), 0) || 0,
      totalViews: product.product_analytics?.reduce((sum: number, analytics: any) => sum + (analytics.views_count || 0), 0) || 0
    }))
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 5) || [];

  const chartData = topProductsData.map(product => ({
    name: product.name.length > 15 ? product.name.substring(0, 15) + '...' : product.name,
    revenue: product.totalRevenue,
    sales: product.totalSales
  }));

  const pieData = [
    { name: 'Active', value: activeProducts, color: '#22c55e' },
    { name: 'Draft', value: products?.filter(p => p.status === 'draft').length || 0, color: '#f59e0b' },
    { name: 'Archived', value: products?.filter(p => p.status === 'archived').length || 0, color: '#6b7280' }
  ];

  return (
    <div className="space-y-6">
      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
            <ShoppingBag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProducts}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalRevenue.toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSales}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{lowStockCount}</div>
            {outOfStockProducts > 0 && (
              <Badge variant="destructive" className="mt-1">
                {outOfStockProducts} out of stock
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top Products by Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Product Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="hsl(var(--primary))"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Category Performance */}
      {categoryStats && categoryStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Category Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="revenue" fill="hsl(var(--secondary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Top Products */}
      <Card>
        <CardHeader>
          <CardTitle>Top Performing Products</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(topProducts || topProductsData)?.slice(0, 6).map((product) => (
              <ProductAnalyticsCard
                key={product.id}
                product={product}
                analytics={{
                  views: product.total_views || product.totalViews || 0,
                  sales: product.total_sales || product.totalSales || 0,
                  revenue: product.total_revenue || product.totalRevenue || 0,
                  conversion_rate: (product.total_views || product.totalViews) > 0 
                    ? ((product.total_sales || product.totalSales) / (product.total_views || product.totalViews)) * 100 
                    : 0,
                  trend: (product.total_revenue || product.totalRevenue) > 1000 ? 'up' : 
                         (product.total_revenue || product.totalRevenue) > 500 ? 'stable' : 'down'
                }}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Low Stock Alert */}
      {lowStockProducts && lowStockProducts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Low Stock Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lowStockProducts.slice(0, 5).map((product) => (
                <div key={product.id} className="flex items-center justify-between p-3 border rounded">
                  <div>
                    <h4 className="font-medium">{product.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      Stock: {product.stock_quantity} remaining
                    </p>
                  </div>
                  <Badge variant="destructive">
                    Low Stock
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductAnalyticsDashboard;
